import { useEffect } from 'react';
import './App.scss';
import { Route, BrowserRouter as Router, Routes } from 'react-router-dom';
import UserService from './services/userService';
import { useAppSelector, useAppDispatch, useRecordAnalyticsWithRetry } from './hooks';
import { MidScreenLoader } from './components/shared/common';
import { Box, Container, Toolbar } from '@mui/material';
import InternalHeader from 'components/shared/layout/Header/InternalHeader';
import PublicHeader from 'components/shared/layout/Header/PublicHeader';
import { UnauthenticatedRoutes } from 'routes';
import { routerFutureFlags } from 'routes';
import { AuthenticatedRoutes } from 'routes';
import { Notification } from 'components/shared/common/Notifications/Notification';
import PageViewTracker from 'routes/PageViewTracker';
import { NotificationModal } from 'components/shared/common/Notifications/NotificationModal';
import { FeedbackModal } from 'components/admin/feedback/FeedbackModal';
import { AppConfig } from 'config';
import { NoAccess } from 'routes';
import { getTenant } from 'services/tenantService';
import { NotFound } from 'routes';
import Footer from 'components/shared/layout/Footer';
import { ZIndex } from 'styles/Theme';
import { TenantState, loadingTenant, saveTenant } from 'redux/slices/tenantSlice';
import { openNotification } from 'services/notificationService/notificationSlice';
import i18n from './i18n';
import DocumentTitle from 'DocumentTitle';

const App = () => {
    const drawerWidth = 280;
    const dispatch = useAppDispatch();
    const roles = useAppSelector((state) => state.user.roles);
    const isLoggedIn = useAppSelector((state) => state.user.authentication.authenticated);
    const authenticationLoading = useAppSelector((state) => state.user.authentication.loading);
    const pathSegments = window.location.pathname.split('/');
    const language = 'en'; // Default language is English, change as needed
    const basename = pathSegments[1].toLowerCase();
    const tenant: TenantState = useAppSelector((state) => state.tenant);

    useEffect(() => {
        UserService.initKeycloak(dispatch);
    }, [dispatch]);

    useRecordAnalyticsWithRetry();

    useEffect(() => {
        sessionStorage.setItem('apiurl', String(AppConfig.apiUrl));
        loadTenant();
    }, [basename, AppConfig.apiUrl]);

    const fetchTenant = async (_basename: string) => {
        if (!_basename) {
            dispatch(loadingTenant(false));
            return;
        }

        try {
            const tenant = await getTenant(_basename);

            const appBaseName = !AppConfig.tenant.isSingleTenantEnvironment ? _basename : '';
            // To be used for API Requests and language translation
            sessionStorage.setItem('tenantId', _basename);
            // To be used for routing
            sessionStorage.setItem('basename', appBaseName);

            dispatch(
                saveTenant({
                    id: _basename,
                    name: tenant.name,
                    logoUrl: tenant.logo_url ?? '',
                    basename: appBaseName,
                }),
            );
        } catch {
            dispatch(loadingTenant(false));
            console.error('Error occurred while fetching Tenant information');
        }
    };

    const loadTenant = () => {
        if (AppConfig.tenant.isSingleTenantEnvironment) {
            fetchTenant(AppConfig.tenant.defaultTenant);
            return;
        }

        if (basename) {
            fetchTenant(basename);
            return;
        }

        if (!basename && AppConfig.tenant.defaultTenant) {
            window.location.replace(`/${AppConfig.tenant.defaultTenant}`);
        }

        dispatch(loadingTenant(false));
    };

    const getTranslationFile = async () => {
        try {
            const translationFile = await import(`./locales/${language}/${tenant.id}.json`);
            return translationFile;
        } catch (error) {
            const defaultTranslationFile = await import(`./locales/${language}/default.json`);
            return defaultTranslationFile;
        }
    };

    const loadTranslation = async () => {
        if (!tenant.id) {
            return;
        }

        i18n.changeLanguage(language); // Set the language for react-i18next

        try {
            const translationFile = await getTranslationFile();
            i18n.addResourceBundle(language, tenant.id, translationFile);
            dispatch(loadingTenant(false));
        } catch (error) {
            dispatch(loadingTenant(false));
            dispatch(
                openNotification({
                    text: 'Error while trying to load texts. Please try again later.',
                    severity: 'error',
                }),
            );
        }
    };

    useEffect(() => {
        loadTranslation();
    }, [tenant.id]);

    if (authenticationLoading || tenant.loading) {
        return <MidScreenLoader />;
    }

    if (!tenant.isLoaded && !tenant.loading) {
        return (
            <Router future={routerFutureFlags}>
                <DocumentTitle />
                <Routes>
                    <Route path="*" element={<NotFound />} />
                </Routes>
            </Router>
        );
    }

    if (!isLoggedIn) {
        return (
            <Router basename={tenant.basename} future={routerFutureFlags}>
                <DocumentTitle />
                <PageViewTracker />
                <Notification />
                <NotificationModal />
                <PublicHeader />
                <UnauthenticatedRoutes />
                <FeedbackModal />
                <Footer />
            </Router>
        );
    }

    if (roles.length === 0) {
        return (
            <Router basename={tenant.basename} future={routerFutureFlags}>
                <DocumentTitle />
                <PublicHeader />
                <Container>
                    <NoAccess />
                </Container>
                <FeedbackModal />
                <Footer />
            </Router>
        );
    }

    return (
        <Router basename={tenant.basename} future={routerFutureFlags}>
            <DocumentTitle />
            <Box sx={{ display: 'flex' }}>
                <InternalHeader drawerWidth={drawerWidth} />
                <Notification />
                <NotificationModal />
                <Box
                    component="main"
                    sx={{
                        flexGrow: 1,
                        width: { xs: '100%', md: `calc(100% - ${drawerWidth}px)` },
                        marginTop: '17px',
                    }}
                >
                    <Toolbar sx={{ marginBottom: { xs: '40px', md: 0 } }} />
                    <AuthenticatedRoutes />
                    <FeedbackModal />
                </Box>
            </Box>
            <Box
                sx={{
                    backgroundColor: 'white',
                    zIndex: ZIndex.footer,
                    position: 'relative',
                    paddingTop: '5em',
                }}
            >
                <Footer />
            </Box>
        </Router>
    );
};
export default App;
